/**
 * PEP V1.0.1+ — commercial CTA labels & Configure entry.
 * Single commercial mode (CTA) drives intent query + submit intent.
 */
import type { ProductCard } from "@itaas/pep-contracts";
import {
  resolveConfigureFinalCtaMode,
  type ConfigureFinalCtaMode,
} from "../configure/configure-final-cta";

export type CommercialCtaMode = ConfigureFinalCtaMode;

export type ConfigureCommercialIntent = "trial" | "devis";

export function resolveCommercialCtaMode(
  product?: ProductCard | null,
): CommercialCtaMode {
  return resolveConfigureFinalCtaMode(product);
}

/** Map CTA mode → Configure commercial intent (single chain). */
export function commercialModeToIntent(
  mode: CommercialCtaMode,
): ConfigureCommercialIntent {
  return mode === "self_service" ? "trial" : "devis";
}

export function commercialIntentForProduct(
  product?: ProductCard | null,
): ConfigureCommercialIntent {
  return commercialModeToIntent(resolveCommercialCtaMode(product));
}

/** Owner WP-02 labels — FR/EN */
export function commercialCtaLabel(
  mode: CommercialCtaMode,
  locale: "fr" | "en" = "fr",
): string {
  if (mode === "self_service") {
    return locale === "en" ? "Create my environment" : "Créer mon environnement";
  }
  if (mode === "early_access") {
    return locale === "en" ? "Join the waitlist" : "Rejoindre la liste d'attente";
  }
  return locale === "en" ? "Request a quote" : "Demander un devis";
}

export function commercialCtaLabelForProduct(
  product: ProductCard | null | undefined,
  locale: "fr" | "en" = "fr",
): string {
  return commercialCtaLabel(resolveCommercialCtaMode(product), locale);
}

export interface ConfigureEntryParams {
  product?: string | null;
  industry?: string | null;
  packageSlug?: string | null;
  /** Commercial intent — drives `return` query (trial | devis). */
  intent?: ConfigureCommercialIntent | null;
  /** Extra query keys preserved from marketing /trial redirects */
  extra?: Record<string, string | null | undefined>;
}

/** Build `/configure` entry — sole commercial hub. Always sets `return` when intent known. */
export function buildConfigureHref(params: ConfigureEntryParams = {}): string {
  const qs = new URLSearchParams();
  if (params.product?.trim()) qs.set("product", params.product.trim());
  if (params.industry?.trim()) qs.set("industry", params.industry.trim());
  if (params.packageSlug?.trim()) qs.set("package", params.packageSlug.trim());
  if (params.intent === "trial" || params.intent === "devis") {
    qs.set("return", params.intent);
  }
  if (params.extra) {
    for (const [k, v] of Object.entries(params.extra)) {
      if (v?.trim() && !qs.has(k)) qs.set(k, v.trim());
    }
  }
  const s = qs.toString();
  return s ? `/configure?${s}` : "/configure";
}

/** Configure href aligned with product CTA mode (SELF_SERVICE → return=trial). */
export function buildConfigureHrefForProduct(
  product: ProductCard,
  opts?: { industry?: string | null; packageSlug?: string | null },
): string {
  return buildConfigureHref({
    product: product.slug,
    industry: opts?.industry,
    packageSlug: opts?.packageSlug,
    intent: commercialIntentForProduct(product),
  });
}

/**
 * Map legacy marketing `/trial?...` query → Configure entry (SELF_SERVICE intent).
 * Drops technical-only keys (step, existing, from).
 */
export function marketingTrialSearchToConfigureHref(
  search: string | URLSearchParams,
): string {
  const src = typeof search === "string" ? new URLSearchParams(search) : search;
  const products = src.get("products");
  const product =
    src.get("product")?.trim() ||
    products?.split(",").map((s) => s.trim()).filter(Boolean)[0] ||
    "";
  return buildConfigureHref({
    product: product || undefined,
    industry: src.get("industry"),
    packageSlug: src.get("package"),
    intent: "trial",
    extra: {
      affiliate: src.get("affiliate") ?? src.get("ref"),
    },
  });
}

/** True when /trial is a technical consumer (handoff / existing session), not marketing. */
export function isTechnicalTrialEntry(
  search: URLSearchParams,
  hasIamSession: boolean,
): boolean {
  if (hasIamSession) return true;
  if (search.get("from") === "engine") return true;
  if (search.get("existing") === "1" && search.get("step")) return true;
  return false;
}
